import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Chip, EmptyState } from "@/components/Shared";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { VEHICLE_TYPE_MAP } from "@/lib/parkingTypes";

export default function ParkingVehiclesTab({ flats }) {
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    const flatMap = useMemo(() => Object.fromEntries(flats.map((f) => [f.id, f])), [flats]);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/parking/vehicles");
            setVehicles(data || []);
        } catch (e) { toast.error(formatError(e)); }
        setLoading(false);
    }
    useEffect(() => { load(); }, []);

    async function deactivate(v) {
        if (!window.confirm(`Deactivate vehicle ${v.registration_number}?`)) return;
        try {
            await api.patch(`/parking-vehicles/${v.id}`, { is_active: false });
            toast.success("Vehicle deactivated");
            load();
        } catch (e) { toast.error(formatError(e)); }
    }

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return vehicles;
        return vehicles.filter((v) => {
            const flat = flatMap[v.flat_id];
            const flatLabel = flat ? `${flat.block}-${flat.number}` : "";
            return v.registration_number?.toLowerCase().includes(term) || flatLabel.toLowerCase().includes(term)
                || (v.owner_name || "").toLowerCase().includes(term);
        });
    }, [vehicles, search, flatMap]);

    if (loading) return <p className="text-sm text-brand-inkSoft py-6">Loading vehicles…</p>;

    return (
        <div>
            <div className="flex items-center gap-3 bg-white border border-brand-line rounded-sm p-4 mb-4">
                <MagnifyingGlass size={18} className="text-brand-inkSoft" />
                <Input data-testid="vehicles-search-input" placeholder="Search registration number, flat or owner…" value={search}
                    onChange={(e) => setSearch(e.target.value)} className="border-0 shadow-none focus-visible:ring-0 px-0" />
            </div>
            {filtered.length === 0 ? (
                <EmptyState title="No vehicles found" description="Residents register their vehicles from My Flat." />
            ) : (
                <div className="overflow-x-auto bg-white border border-brand-line rounded-sm">
                    <table className="w-full text-sm" data-testid="vehicles-table">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-overline text-brand-inkSoft border-b border-brand-line">
                                <th className="py-3 px-4">Registration</th>
                                <th className="py-3 px-4">Type</th>
                                <th className="py-3 px-4">Make / Model</th>
                                <th className="py-3 px-4">Owner</th>
                                <th className="py-3 px-4">Flat</th>
                                <th className="py-3 px-4">Status</th>
                                <th className="py-3 px-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((v) => {
                                const flat = flatMap[v.flat_id];
                                return (
                                    <tr key={v.id} data-testid={`vehicle-row-${v.id}`} className="border-b border-brand-line/60 last:border-0">
                                        <td className="py-3 px-4 font-mono font-medium text-brand-ink">{v.registration_number}</td>
                                        <td className="py-3 px-4">{VEHICLE_TYPE_MAP[v.vehicle_type]?.label || v.vehicle_type}</td>
                                        <td className="py-3 px-4 text-brand-inkSoft">{[v.make, v.model].filter(Boolean).join(" ") || "—"}</td>
                                        <td className="py-3 px-4 text-brand-inkSoft">{v.owner_name || "—"}</td>
                                        <td className="py-3 px-4">{flat ? `${flat.block}-${flat.number}` : "—"}</td>
                                        <td className="py-3 px-4">
                                            <Chip variant={v.is_active !== false ? "success" : "neutral"}>{v.is_active !== false ? "Active" : "Inactive"}</Chip>
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            {v.is_active !== false && (
                                                <Button size="sm" variant="outline" data-testid={`vehicle-deactivate-btn-${v.id}`}
                                                    onClick={() => deactivate(v)} className="rounded-full border-brand-line text-xs h-7">Deactivate</Button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
